import { useState, useEffect } from 'react';
import { api } from '../api';

const EMPTY = {
  employeeId: '', firstName: '', lastName: '', gender: 'Male', dob: '',
  phone: '', email: '', address: '', qualification: '', experienceYrs: '',
  subject: { id: '' }, joiningDate: '', basicSalary: '', status: 'Active'
};

function fullName(t) { return t ? `${t.firstName || ''} ${t.lastName || ''}`.trim() : ''; }
function initials(t) {
  const n = fullName(t);
  return n.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '??';
}

export default function Teachers() {
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('list');
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([api.getTeachers(), api.getSubjects()])
      .then(([t, s]) => { setTeachers(t); setSubjects(s); })
      .catch(() => setErr('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = teachers.filter(t =>
    !search || fullName(t).toLowerCase().includes(search.toLowerCase()) ||
    t.subject?.name?.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => { setForm(EMPTY); setEditId(null); setTab('add'); setErr(''); };
  const openEdit = (t) => {
    setForm({
      ...t,
      dob: t.dob ? t.dob.split('T')[0] : '',
      joiningDate: t.joiningDate ? t.joiningDate.split('T')[0] : '',
      subject: { id: t.subject?.id ?? '' }
    });
    setEditId(t.id);
    setTab('add');
    setErr('');
  };

  const handleSave = async () => {
    if (!form.firstName) { setErr('First name is required'); return; }
    const payload = {
      ...form,
      subject: form.subject?.id ? { id: parseInt(form.subject.id) } : null
    };
    try {
      if (editId) await api.updateTeacher(editId, payload);
      else await api.createTeacher(payload);
      window.location.reload();
    } catch (e) { setErr(e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this teacher?')) return;
    await api.deleteTeacher(id);
    window.location.reload();
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (loading) return <div className="loading-msg">Loading teachers…</div>;

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'list' ? ' active' : ''}`} onClick={() => setTab('list')}>Teacher List</button>
        <button className={`tab${tab === 'add' ? ' active' : ''}`} onClick={openAdd}>Add Teacher</button>
      </div>

      {/* ── LIST TAB ── */}
      {tab === 'list' && (
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Teaching Staff</div>
              <div className="card-subtitle">{filtered.length} teachers</div>
            </div>
            <div className="card-actions">
              <div className="search-bar">
                <i className="fas fa-search"></i>
                <input type="text" placeholder="Search by name or subject…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <button className="btn btn-primary btn-sm" onClick={openAdd}>
                <i className="fas fa-plus"></i> Add Teacher
              </button>
            </div>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th><th>Name</th><th>Employee ID</th><th>Subject</th>
                  <th>Qualification</th><th>Phone</th><th>Salary</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: '#888', padding: 30 }}>No teachers found</td></tr>
                )}
                {filtered.map((t, i) => (
                  <tr key={t.id}>
                    <td>{i + 1}</td>
                    <td>
                      <div className="chip">
                        <div className="chip-avatar">{initials(t)}</div>
                        <span className="chip-text">{fullName(t)}</span>
                      </div>
                    </td>
                    <td>{t.employeeId || '—'}</td>
                    <td>{t.subject?.name || '—'}</td>
                    <td>{t.qualification}</td>
                    <td>{t.phone}</td>
                    <td>₹{Number(t.basicSalary || 0).toLocaleString('en-IN')}</td>
                    <td>
                      <span className={`badge ${t.status?.toLowerCase() === 'active' ? 'badge-green' : 'badge-orange'}`}>
                        {t.status}
                      </span>
                    </td>
                    <td>
                      <div className="card-actions">
                        <button className="btn btn-outline btn-sm btn-icon" onClick={() => openEdit(t)} title="Edit">
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(t.id)} title="Delete">
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── ADD / EDIT TAB ── */}
      {tab === 'add' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">{editId ? 'Edit Teacher' : 'Add New Teacher'}</div>
          </div>
          <div className="card-body">
            {err && <div className="error-msg">{err}</div>}
            <div className="form-grid cols-3">
              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input className="form-control" value={form.firstName} onChange={e => set('firstName', e.target.value)} placeholder="First name" />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name *</label>
                <input className="form-control" value={form.lastName} onChange={e => set('lastName', e.target.value)} placeholder="Last name" />
              </div>
              <div className="form-group">
                <label className="form-label">Employee ID</label>
                <input className="form-control" value={form.employeeId} onChange={e => set('employeeId', e.target.value)} placeholder="Auto-generated" />
              </div>
              <div className="form-group">
                <label className="form-label">Gender</label>
                <select className="form-control" value={form.gender} onChange={e => set('gender', e.target.value)}>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Date of Birth</label>
                <input className="form-control" type="date" value={form.dob} onChange={e => set('dob', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Date of Joining *</label>
                <input className="form-control" type="date" value={form.joiningDate} onChange={e => set('joiningDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Subject *</label>
                <select className="form-control" value={form.subject?.id ?? ''}
                  onChange={e => setForm(f => ({ ...f, subject: { id: e.target.value } }))}>
                  <option value="">-- Select Subject --</option>
                  {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Qualification</label>
                <input className="form-control" value={form.qualification} onChange={e => set('qualification', e.target.value)} placeholder="e.g. M.Sc, B.Ed" />
              </div>
              <div className="form-group">
                <label className="form-label">Experience (Years)</label>
                <input className="form-control" type="number" value={form.experienceYrs} onChange={e => set('experienceYrs', e.target.value)} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label">Phone *</label>
                <input className="form-control" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Monthly Salary *</label>
                <input className="form-control" type="number" value={form.basicSalary} onChange={e => set('basicSalary', e.target.value)} placeholder="₹" />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div className="form-group full">
                <label className="form-label">Address</label>
                <textarea className="form-control" value={form.address} onChange={e => set('address', e.target.value)} />
              </div>
            </div>
            <div className="form-actions">
              <button className="btn btn-outline" onClick={() => { setTab('list'); setEditId(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>
                <i className="fas fa-save"></i> {editId ? 'Update Teacher' : 'Save Teacher'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
